import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import ReactDOM from 'react-dom';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi, initiativesApi, organizationalUnitsApi } from '../../services/apiService';
import { KPIType, Frequency, Polarity } from '../../types';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
  validateKpiTargetConfig,
} from './components/KpiAnnualTargetSection';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useTranslation } from 'react-i18next';
import { normalizeArabicNumbers } from '../../utils/rtl';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
type RouteProps = RouteProp<MainStackParamList, 'AddInitiativeKPI'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBg: colors.card,
    itemActiveBg: isDark ? '#451A1A' : tokens.colors.raw.red["50"],
  };

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      setIsOpen(false);
    };
    const handleScroll = () => {
      setIsOpen(false);
    };
    document.addEventListener('mouseup', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        start: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: tokens.spacing.absolute["px"],
        borderColor: dropdownTheme.menuBorder,
        borderRadius: tokens.radii.absolute["radius-12"],
        paddingVertical: tokens.spacing.absolute["2"],
        zIndex: 99999,
        elevation: 99999,
        maxHeight: 200,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
        <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.dropdownItem,
                { backgroundColor: dropdownTheme.itemBg },
                value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
              ]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[
                styles.dropdownItemText,
                { color: dropdownTheme.text },
                value === option.value && styles.dropdownItemTextActive
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
        ]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function AddInitiativeKPIScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { initiativeId, kpiId } = route.params;
  const isEditMode = !!kpiId;
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    checkboxBorder: isDark ? '#4B5563' : tokens.colors.raw.gray["300"],
    checkboxBg: colors.card,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    weight: '30',
    use_weight: true,
    description: '',
    kpiType: '' as KPIType | '',
    polarity: '' as Polarity | '',
    startDate: '',
    unit: '%',
    frequency: 'yearly' as Frequency | '',
    formula: '',
    targetValue: '0.0',
    worstAcceptableValue: '',
    dataSource: '',
    // Annual target grid (config object handled by the shared section)
    target: emptyKpiTargetConfig as KpiTargetConfig,
    responsibleUnits: [] as string[],
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [targetErrors, setTargetErrors] = useState<Record<string, string>>({});
  const [initiativeUnitOptions, setInitiativeUnitOptions] = useState<{ value: string; label: string }[]>([]);

  // Fetch the parent initiative's saved responsibility units to scope the
  // options list (this standalone screen has no organizational-units field
  // of its own — units come from the initiative it belongs to).
  useEffect(() => {
    let active = true;
    (async () => {
      if (!initiativeId) return;
      try {
        const init = await initiativesApi.get(initiativeId);
        const all = await organizationalUnitsApi.list({ page_size: 9999 });
        if (!active) return;
        const ids = (init.responsible_units || []).map(String);
        setInitiativeUnitOptions(
          (all.results || [])
            .filter((u) => ids.includes(String(u.id)))
            .map((u) => ({ value: u.id.toString(), label: translate(u.name_ar || u.name) }))
        );
      } catch (error) {
        // Non-fatal: leave options empty, saved-unit preservation below still
        // shows any previously selected units by raw value.
      }
    })();
    return () => {
      active = false;
    };
  }, [initiativeId]);

  // Edit mode: load the existing KPI and prefill the form (incl. target grid).
  useEffect(() => {
    if (!kpiId) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const kpi: any = await kpisApi.get(kpiId);
        if (!active) return;
        setFormData({
          name: kpi.name || '',
          weight: kpi.weight != null ? String(kpi.weight) : '30',
          use_weight: kpi.use_weight ?? true,
          description: kpi.description || '',
          kpiType: (kpi.kpi_type || '') as KPIType | '',
          polarity: (kpi.polarity || '') as Polarity | '',
          startDate: kpi.measurement_start_date || '',
          unit: kpi.unit || '',
          frequency: (kpi.frequency || '') as Frequency | '',
          formula: kpi.measurement_formula || '',
          targetValue: kpi.target_value != null ? String(kpi.target_value) : '0.0',
          worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
          dataSource: kpi.owner || kpi.data_source || '',
          target: configFromKpi(kpi),
          responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
        });
      } catch (error) {
        if (active) showError(t('kpis.loadFailed'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [kpiId]);

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // KPI Type options
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'عدد', label: t('kpis.number') },
    { value: 'درهم', label: t('kpis.currency') },
    { value: 'يوم', label: t('kpis.day') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.kpiNameRequired');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.kpiDescriptionRequired');
    }
    if (!formData.kpiType) {
      newErrors.kpiType = t('validation.kpiTypeRequired');
    }
    if (!formData.polarity) {
      newErrors.polarity = t('validation.polarityRequired');
    }
    // Measurement + grid errors are owned by the shared section (keyed by
    // validation-message key so the section can translate them).
    const secErrors: Record<string, string> = {};
    if (!formData.unit) secErrors.unit = 'unitRequired';
    if (!formData.frequency) secErrors.frequency = 'frequencyRequired';
    const tErrors = { ...secErrors, ...validateKpiTargetConfig(formData.target, { startDate: formData.startDate }) };
    setTargetErrors(tErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(tErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setLoading(true);
    try {
      const kpiData = {
        name: formData.name,
        description: formData.description,
        initiative: initiativeId,
        kpi_type: formData.kpiType as KPIType,
        polarity: formData.polarity as Polarity,
        unit: formData.unit,
        frequency: formData.frequency as Frequency,
        weight: formData.weight,
        use_weight: formData.use_weight,
        owner: formData.dataSource,
        data_source: formData.dataSource,
        measurement_formula: formData.formula,
        measurement_start_date: formData.startDate || null,
        responsible_units: formData.responsibleUnits.map(Number),
        // target_value is mirrored server-side from the grid's overall total.
        ...buildKpiTargetPayload(formData.target, {
          unit: formData.unit,
          frequency: formData.frequency,
          startDate: formData.startDate,
        }),
      };

      if (isEditMode && kpiId) {
        await kpisApi.update(kpiId, kpiData);
        showSuccess(t('kpis.updateSuccess'));
      } else {
        await kpisApi.create(kpiData);
        showSuccess(t('kpis.kpiAddedSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      console.error('Error saving KPI:', error);
      const errorMessage = error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || t('kpis.kpiAddFailed');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData },
    ready: !loading,
    onExit: handleCancel,
  });

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: containerPadding }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('nav.home') },
            { label: t('initiatives.initiativeDetails'), route: 'InitiativeDetail', params: { id: initiativeId } },
            { label: t('kpis.addPerformanceIndicator') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('kpis.editKPI') : t('kpis.addKPIForInitiative')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            {/* KPI Name & Weight Row */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={styles.setWeightButton}>
                    <Text style={styles.setWeightButtonText}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={[styles.weightInput, !formData.use_weight && { opacity: 0.5 }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                    editable={formData.use_weight}
                  />
                </View>
                {/* Use Weight Toggle */}
                <TouchableOpacity
                  style={styles.useWeightToggle}
                  onPress={() => setFormData(prev => ({ ...prev, use_weight: !prev.use_weight }))}
                >
                  <View style={[styles.checkbox, formData.use_weight && styles.checkboxChecked]}>
                    {formData.use_weight && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.useWeightLabel}>{t('kpis.useWeightInCalculation')}</Text>
                </TouchableOpacity>
              </View>

              {/* KPI Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, errors.name && styles.inputError]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder=""
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={styles.label}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, errors.description && styles.inputError]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>
          </View>

          {/* Classification Section */}
          <View style={[styles.section, { zIndex: 30 }]}>
            <Text style={styles.sectionTitle}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column' }]}>
              {/* Polarity Field */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={formData.polarity}
                  options={polarityOptions}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateField('polarity', v)}
                />
                {errors.polarity && <Text style={styles.errorText}>{errors.polarity}</Text>}
              </View>

              {/* KPI Type Field */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={formData.kpiType}
                  options={kpiTypeOptions}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateField('kpiType', v)}
                />
                {errors.kpiType && <Text style={styles.errorText}>{errors.kpiType}</Text>}
              </View>
            </View>

            {/* Implementation Responsibility Units (scoped to the parent initiative's saved units) */}
            <View style={{ marginTop: tokens.spacing.absolute['3'], zIndex: 5 }}>
              <Text style={[styles.label, { marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
              <MultiSelectDropdown
                values={formData.responsibleUnits}
                options={(() => {
                  const opts = [...initiativeUnitOptions];
                  formData.responsibleUnits.forEach((v) => {
                    if (!opts.find((o) => o.value === v)) {
                      // Preserve an already-saved unit even if it falls outside
                      // the initiative's current set (e.g. initiative units
                      // changed after this KPI was last saved).
                      opts.push({ value: v, label: v });
                    }
                  });
                  return opts;
                })()}
                placeholder={t('initiatives.selectResponsibleUnits')}
                onChange={(vals) => setFormData((prev) => ({ ...prev, responsibleUnits: vals }))}
              />
            </View>
          </View>

          {/* Measurement (القياس) + annual targets — shared section */}
          <View style={{ zIndex: 20 }}>
            <KpiAnnualTargetSection
              config={formData.target}
              onChange={(patch) => setFormData((prev) => ({ ...prev, target: { ...prev.target, ...patch } }))}
              startDate={formData.startDate}
              unit={formData.unit}
              frequency={formData.frequency}
              formula={formData.formula}
              dataSource={formData.dataSource}
              onMeasurementChange={(field, value) => updateField(field, value)}
              errors={targetErrors}
            />
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <Button variant="primary" onPress={handleSave} loading={loading}>
              {t('common.save')}
            </Button>
            <Button variant="secondary" onPress={requestCancel} disabled={loading}>
              {t('common.cancel')}
            </Button>
          </View>
        </View>
      </ScrollView>
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Field styles
  field: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
  },
  // Input styles
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  setWeightButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  setWeightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Use Weight Toggle
  useWeightToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: tokens.spacing.absolute["2"],
    gap: tokens.spacing.absolute["2"],
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: tokens.colors.raw.gray["300"],
    borderRadius: tokens.radii.absolute["radius-4"],
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
  },
  checkboxChecked: {
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    borderColor: tokens.colors.semantic.general["Main Color"],
  },
  checkmark: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '700',
  },
  useWeightLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
  },
  dropdownButtonOpen: {
    borderColor: tokens.colors.semantic.general["Main Color"],
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.gray["400"],
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 99999,
    maxHeight: 200,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
  },
  dropdownItemActive: {
    backgroundColor: tokens.colors.raw.red["50"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownItemTextActive: {
    color: tokens.colors.semantic.general["Main Color"],
    fontWeight: '600',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
  },
  // Error styles
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
});

const styles = getStyles(true);
